import { diagnostic } from '../../utils/diagnostics';
import { Capacitor } from '@capacitor/core';
import React, {
  useEffect,
  useState,
} from 'react';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import KidAuthLayout from '../../components/Layout/KidAuthLayout';
import AppDownloadCard from '../../components/Downloads/AppDownloadCard';
import kidArtwork from '../../assets/icons/portal/kid-login.png';
import Input from '../../components/UI/Input';

import {
  useKidAuth,
} from '../../contexts/KidAuthContext';

import {
  kidAuth,
  dbKid,
  debugRestPingKid,
} from '../../services/supabase';

const KidLogin = () => {
  const navigate =
    useNavigate();

  const location =
    useLocation();

  const requestedPath =
    location.state?.from?.pathname;

  const requestedSearch =
    location.state?.from?.search || '';

  const requestedHash =
    location.state?.from?.hash || '';

  const kidDestination =
    typeof requestedPath === 'string' &&
    requestedPath.startsWith('/kid/') &&
    requestedPath !== '/kid/login'
      ? requestedPath
          + requestedSearch
          + requestedHash
      : '/kid/dashboard';

  const {
    loginKid,
  } =
    useKidAuth();

  const [
    loginId,
    setLoginId,
  ] =
    useState('');

  const [
    password,
    setPassword,
  ] =
    useState('');

  const [
    loading,
    setLoading,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState('');

  /*
   * ---------------------------------------------------------
   * EXISTING KID SESSION
   * ---------------------------------------------------------
   */

  useEffect(() => {
    let mounted =
      true;

    try {
      const accessMessage = window.sessionStorage.getItem(
        'mypiggy:kid-access-message'
      );

      if (accessMessage) {
        setError(accessMessage);
        window.sessionStorage.removeItem(
          'mypiggy:kid-access-message'
        );
      }
    } catch {
      diagnostic('warn', 'kid.auth.storage_unavailable');
    }

    (async () => {
      const {
        data,
      } =
        await kidAuth.getUser();

      if (!mounted) {
        return;
      }

      if (
        data?.user?.id
      ) {
        navigate(
          kidDestination,
          {
            replace: true,
          }
        );
      }
    })();

    return () => {
      mounted =
        false;
    };
  }, [kidDestination, navigate]);

  /*
   * ---------------------------------------------------------
   * FRIENDLY LOGIN ERROR
   * ---------------------------------------------------------
   */

  const getLoginErrorMessage =
    (err) => {
      const status =
        err?.status;

      const code =
        err?.code;

      if (
        status === 429 ||
        code ===
          'KID_LOGIN_RATE_LIMITED'
      ) {
        return (
          'Too many login attempts. ' +
          'Please wait 15 minutes and try again.'
        );
      }

      if (
        status === 401 ||
        code ===
          'KID_LOGIN_INVALID_CREDENTIALS'
      ) {
        /*
         * Keep this deliberately generic.
         *
         * We do not tell a user whether the Login ID
         * or PIN specifically was incorrect.
         */
        return (
          'Login ID or password is incorrect.'
        );
      }

      if (
        status === 400 ||
        code ===
          'KID_LOGIN_INVALID_REQUEST'
      ) {
        return (
          err?.message ||
          'Please check your login information and try again.'
        );
      }

      return (
        "We couldn't sign you in right now. " +
        'Please try again later.'
      );
    };

  /*
   * ---------------------------------------------------------
   * LOGIN
   * ---------------------------------------------------------
   */

  const handleSubmit =
    async (e) => {
      e.preventDefault();

      if (loading) {
        return;
      }

      setError('');
      setLoading(true);

      try {
        const normalizedLoginId =
          loginId
            .trim()
            .toUpperCase();

        const normalizedPassword =
          password;

        /*
         * Stage 2B Login ID examples:
         *
         *   9AD187-JOHN
         *   9AD187-JOHN-P
         *   9AD187-JOHN-P2
         *
         * The Edge Function repeats validation server-side.
         */
        if (
          !/^[A-Z0-9]+-[A-Z0-9]+(?:-[A-Z0-9]+)?$/.test(
            normalizedLoginId
          )
        ) {
          throw Object.assign(
            new Error(
              'Enter a valid MyPiggy Login ID, such as 9AD187-JOHN.'
            ),
            {
              status: 400,
              code:
                'KID_LOGIN_INVALID_REQUEST',
            }
          );
        }

        if (
          normalizedPassword.length < 6 || normalizedPassword.length > 64
        ) {
          throw Object.assign(
            new Error(
              'Enter your 6 to 64 character password.'
            ),
            {
              status: 400,
              code:
                'KID_LOGIN_INVALID_REQUEST',
            }
          );
        }

        diagnostic('debug', 'kid.auth.login_started');

        const result =
          await loginKid({
            loginId:
              normalizedLoginId,

            password:
              normalizedPassword,
          });

        diagnostic('debug', 'kid.auth.login_returned');

        if (
          !result?.success
        ) {
          throw new Error(
            result?.error ||
              'Login failed'
          );
        }

        /*
         * Existing debug/session verification retained.
         */
        diagnostic('debug', 'kid.auth.verification_started');

        await debugRestPingKid();

        diagnostic('debug', 'kid.auth.verification_completed');

        /*
         * Confirm kid session exists.
         */
        diagnostic('debug', 'kid.auth.session_check_started');

        const {
          data:
            kidUserRes,

          error:
            kidUserErr,
        } =
          await kidAuth.getUser();

        diagnostic('debug', 'kid.auth.session_checked');

        if (
          kidUserErr
        ) {
          throw kidUserErr;
        }

        if (
          !kidUserRes
            ?.user
            ?.id
        ) {
          throw new Error(
            'No authenticated kid user found after login'
          );
        }

        /*
         * Confirm corresponding active kid profile exists.
         */
        diagnostic('debug', 'kid.profile.load_started');

        const kidRow =
          await dbKid
            .getKidByAuthUserId(
              kidUserRes
                .user
                .id
            );

        diagnostic('debug', 'kid.profile.load_completed');

        if (!kidRow) {
          throw new Error(
            'Kid profile not found.'
          );
        }

        diagnostic('debug', 'kid.auth.navigation_started');

        navigate(
          kidDestination,
          {
            replace: true,
          }
        );
      } catch (err) {
        diagnostic('error', 'kid.auth.login_failed');

        setError(
          getLoginErrorMessage(
            err
          )
        );

        /*
         * Clear only the password.
         *
         * Keeping the Login ID makes correcting a mistyped
         * password easier for a child.
         */
        setPassword('');
      } finally {
        setLoading(false);
      }
    };

  return (
    <KidAuthLayout>
      <section aria-labelledby="kid-login-heading" className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-6 text-center">
          <img src={kidArtwork} alt="" className="mx-auto mb-3 h-20 w-20 object-contain lg:hidden" />
          <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.14em] text-pink-700">MyPiggy Kids</p>
          <h1 id="kid-login-heading" className="text-3xl font-extrabold tracking-tight text-teal-950">Kid Login</h1>
          <p className="mt-2 text-base leading-relaxed text-slate-600">Sign in to see your money, goals, and progress.</p>
        </div>

        <form onSubmit={handleSubmit} className="kid-auth-form space-y-5" aria-busy={loading}>
          <Input
            label="MyPiggy Login ID"
            name="loginId"
            value={loginId}
            onChange={(e) => {
              setLoginId(e.target.value.replace(/\s/g, '').toUpperCase());
              if (error) setError('');
            }}
            placeholder="e.g. 9AD187-JOHN"
            helperText="Your parent can find this on their MyPiggy dashboard."
            required
            autoComplete="username"
            spellCheck="false"
            autoCapitalize="characters"
            disabled={loading}
          />
          <Input
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value.slice(0, 64));
              if (error) setError('');
            }}
            placeholder="Enter your password"
            maxLength="64"
            required
            autoComplete="current-password"
            disabled={loading}
          />
          {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
          <button type="submit" disabled={loading} className="min-h-12 w-full rounded-xl bg-pink-700 px-5 py-3 font-extrabold text-white transition hover:bg-pink-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="mt-6 rounded-2xl border border-teal-100 bg-teal-50/60 px-4 py-4 text-center">
          <p className="font-extrabold text-teal-950">Need a MyPiggy login?</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">Ask your parent for your MyPiggy Login ID and password.</p>
        </div>
        {!(Capacitor.isNativePlatform() && import.meta.env.VITE_APP_AUDIENCE === 'kids') && (
          <div className="mt-4 text-center">
            <button type="button" onClick={() => navigate('/parent/login')} disabled={loading} className="min-h-11 rounded-md px-2 text-sm font-bold text-slate-600 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-700 disabled:opacity-50">← Back to Parent Login</button>
          </div>
        )}
      </section>
      {!Capacitor.isNativePlatform() && <div className="mt-5"><AppDownloadCard audience="kids" /></div>}
    </KidAuthLayout>
  );
};

export default KidLogin;

